import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { BarChart3, TrendingUp, Zap, Globe, Target, Download, Clock, GitBranch, Users, Gift, ArrowRight, FileDown } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#0055FF', '#10B981', '#F59E0B', '#EF4444', '#6366F1', '#EC4899'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-xs">
      <p className="text-[#A1A1AA] mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-medium">{p.name}: {p.value}</p>
      ))}
    </div>
  );
};

const AnalyticsDashboard = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [keywords, setKeywords] = useState([]);
  const [urls, setUrls] = useState([]);
  const [referralData, setReferralData] = useState(null);
  const [hourlyData, setHourlyData] = useState([]);
  const [endpointData, setEndpointData] = useState(null);
  const [period, setPeriod] = useState(30);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [overview, kw, ur, referrals, hourly, endpoints] = await Promise.all([
        axios.get(`${API_URL}/api/analytics/overview?days=${period}`, authHeaders),
        axios.get(`${API_URL}/api/analytics/top-keywords?days=${period}`, authHeaders),
        axios.get(`${API_URL}/api/analytics/top-urls?days=${period}`, authHeaders),
        axios.get(`${API_URL}/api/analytics/referral-stats?days=${period}`, authHeaders),
        axios.get(`${API_URL}/api/analytics/hourly-breakdown?days=${Math.min(period, 30)}`, authHeaders),
        axios.get(`${API_URL}/api/analytics/endpoint-breakdown?days=${period}`, authHeaders)
      ]);
      setData(overview.data);
      setKeywords(kw.data);
      setUrls(ur.data);
      setReferralData(referrals.data);
      setHourlyData(hourly.data);
      setEndpointData(endpoints.data);
    } catch (err) {
      console.error('Analytics fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [token, period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const exportCSV = () => {
    if (!data) return;
    const rows = [['Data', 'Chiamate API', 'Chiamate B2B']];
    const dailyMap = {};
    data.daily_calls?.forEach(d => { dailyMap[d.date] = { ...dailyMap[d.date], calls: d.calls }; });
    data.daily_api_key_calls?.forEach(d => { dailyMap[d.date] = { ...dailyMap[d.date], b2b: d.calls }; });
    Object.entries(dailyMap).sort().forEach(([date, vals]) => {
      rows.push([date, vals.calls || 0, vals.b2b || 0]);
    });
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics_${period}d.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportFullCSV = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/analytics/export-full?days=${period}`, {
        ...authHeaders,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics_completo_${period}gg.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
    }
  };

  if (loading) {
    return (
      <div className="py-12 text-center">
        <div className="w-8 h-8 border-2 border-[#0055FF] border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-[#A1A1AA] mt-4 text-sm">Caricamento analytics...</p>
      </div>
    );
  }

  if (!data) return null;

  // Merge daily data for chart
  const chartData = [];
  const allDays = new Set([
    ...(data.daily_calls?.map(d => d.date) || []),
    ...(data.daily_api_key_calls?.map(d => d.date) || [])
  ]);
  const callsMap = Object.fromEntries((data.daily_calls || []).map(d => [d.date, d.calls]));
  const b2bMap = Object.fromEntries((data.daily_api_key_calls || []).map(d => [d.date, d.calls]));
  [...allDays].sort().forEach(day => {
    chartData.push({ date: day.substring(5), calls: callsMap[day] || 0, b2b: b2bMap[day] || 0 });
  });

  const creditsPct = data.credits_total > 0 ? Math.round((data.credits_used / data.credits_total) * 100) : 0;

  const sections = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'referral', label: 'Referral', icon: Gift },
    { id: 'hourly', label: 'Per Ora', icon: Clock },
    { id: 'endpoints', label: 'Endpoints', icon: GitBranch }
  ];

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-6 h-6 text-[#0055FF]" />
          <h2 className="font-['Outfit'] text-lg font-medium">Analytics</h2>
        </div>
        <div className="flex items-center gap-2">
          {[7, 30, 90].map(d => (
            <button
              key={d}
              onClick={() => setPeriod(d)}
              className={`px-3 py-1.5 rounded-lg text-xs transition-colors ${period === d ? 'bg-[#0055FF] text-white' : 'bg-white/5 text-[#A1A1AA] hover:text-white'}`}
              data-testid={`period-${d}d`}
            >
              {d}gg
            </button>
          ))}
          <Button onClick={exportCSV} variant="outline" size="sm" className="border-white/10 text-xs" data-testid="export-csv-btn">
            <Download className="w-3 h-3 mr-1" /> CSV
          </Button>
          <Button onClick={exportFullCSV} variant="outline" size="sm" className="border-white/10 text-xs" data-testid="export-full-csv-btn">
            <FileDown className="w-3 h-3 mr-1" /> Report
          </Button>
        </div>
      </div>

      {/* Sub-navigation */}
      <div className="flex gap-1 bg-white/5 rounded-lg p-1">
        {sections.map(s => (
          <button
            key={s.id}
            onClick={() => setActiveSection(s.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs transition-colors ${
              activeSection === s.id ? 'bg-[#0055FF] text-white' : 'text-[#A1A1AA] hover:text-white'
            }`}
            data-testid={`analytics-section-${s.id}`}
          >
            <s.icon className="w-3 h-3" />
            {s.label}
          </button>
        ))}
      </div>

      {/* ===== OVERVIEW SECTION ===== */}
      {activeSection === 'overview' && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Chiamate API', value: data.total_api_calls, icon: Zap, color: '#0055FF' },
              { label: 'Chiamate B2B', value: data.total_b2b_api_calls, icon: Target, color: '#6366F1' },
              { label: 'Demo Pubbliche', value: data.total_demo_calls, icon: Globe, color: '#F59E0B' },
              { label: 'Crediti Rimasti', value: data.credits_remaining, icon: TrendingUp, color: data.credits_remaining < 500 ? '#EF4444' : '#10B981' }
            ].map((stat, i) => (
              <div key={i} className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
                <div className="flex items-center gap-2 mb-2">
                  <stat.icon className="w-4 h-4" style={{ color: stat.color }} />
                  <span className="text-[10px] text-[#A1A1AA] uppercase tracking-wider">{stat.label}</span>
                </div>
                <span className="text-2xl font-['Outfit'] font-semibold">{stat.value.toLocaleString()}</span>
              </div>
            ))}
          </div>

          {/* Credits Bar */}
          <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-[#A1A1AA]">Utilizzo Crediti</span>
              <span className="text-xs font-mono">{data.credits_used.toLocaleString()} / {data.credits_total.toLocaleString()}</span>
            </div>
            <div className="h-3 bg-white/5 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${Math.min(100, creditsPct)}%`,
                  backgroundColor: creditsPct > 80 ? '#EF4444' : creditsPct > 50 ? '#F59E0B' : '#10B981'
                }}
              />
            </div>
            <span className="text-[10px] text-[#A1A1AA] mt-1 block">{creditsPct}% utilizzato</span>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
              <h4 className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-4">Chiamate API nel tempo</h4>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="gradCalls" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0055FF" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#0055FF" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gradB2B" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366F1" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#6366F1" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#A1A1AA' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: '#A1A1AA' }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="calls" name="Plugin API" stroke="#0055FF" fill="url(#gradCalls)" strokeWidth={2} />
                    <Area type="monotone" dataKey="b2b" name="B2B API" stroke="#6366F1" fill="url(#gradB2B)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-[#A1A1AA] text-sm">Nessun dato per il periodo selezionato</div>
              )}
            </div>

            <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
              <h4 className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-4">Per tipo</h4>
              {data.calls_by_type?.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={150}>
                    <PieChart>
                      <Pie data={data.calls_by_type} dataKey="count" nameKey="type" cx="50%" cy="50%" outerRadius={60} innerRadius={35}>
                        {data.calls_by_type.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-1 mt-2">
                    {data.calls_by_type.slice(0, 5).map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-[10px]">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                        <span className="text-[#A1A1AA] flex-1 truncate">{item.type}</span>
                        <span className="font-medium">{item.count}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="h-[150px] flex items-center justify-center text-[#A1A1AA] text-sm">Nessun dato</div>
              )}
            </div>
          </div>

          {/* Top Keywords & URLs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
              <h4 className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-3 flex items-center gap-2">
                <Target className="w-3 h-3" /> Top Keywords Analizzate
              </h4>
              {keywords.length > 0 ? (
                <div className="space-y-2">
                  {keywords.slice(0, 8).map((kw, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-[10px] text-[#A1A1AA] w-4">{i + 1}.</span>
                      <div className="flex-1 h-5 bg-white/5 rounded-full overflow-hidden relative">
                        <div className="h-full rounded-full" style={{
                          width: `${Math.max(10, (kw.count / (keywords[0]?.count || 1)) * 100)}%`,
                          backgroundColor: COLORS[i % COLORS.length] + '40'
                        }} />
                        <span className="absolute inset-0 flex items-center px-2 text-[10px] text-white truncate">{kw.keyword}</span>
                      </div>
                      <span className="text-[10px] font-mono text-[#A1A1AA] w-6 text-right">{kw.count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-[#A1A1AA] py-4 text-center">Nessuna keyword analizzata</p>
              )}
            </div>

            <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
              <h4 className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-3 flex items-center gap-2">
                <Globe className="w-3 h-3" /> Top URL Analizzati
              </h4>
              {urls.length > 0 ? (
                <div className="space-y-2">
                  {urls.slice(0, 8).map((u, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-[10px] text-[#A1A1AA] w-4">{i + 1}.</span>
                      <span className="text-[10px] text-white flex-1 truncate">{u.url}</span>
                      <span className="text-[10px] font-mono text-[#A1A1AA]">{u.count}x</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-[#A1A1AA] py-4 text-center">Nessun URL analizzato</p>
              )}
            </div>
          </div>
        </>
      )}

      {/* ===== REFERRAL SECTION ===== */}
      {activeSection === 'referral' && referralData && (
        <>
          {/* Funnel Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Invitati', value: referralData.total_invited, icon: Users, color: '#0055FF' },
              { label: 'Registrati', value: referralData.registered, icon: Users, color: '#F59E0B' },
              { label: 'Convertiti', value: referralData.converted, icon: Gift, color: '#10B981' },
              { label: 'Tasso Conv.', value: `${referralData.conversion_rate}%`, icon: TrendingUp, color: '#6366F1' }
            ].map((stat, i) => (
              <div key={i} className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
                <div className="flex items-center gap-2 mb-2">
                  <stat.icon className="w-4 h-4" style={{ color: stat.color }} />
                  <span className="text-[10px] text-[#A1A1AA] uppercase tracking-wider">{stat.label}</span>
                </div>
                <span className="text-2xl font-['Outfit'] font-semibold">{typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}</span>
              </div>
            ))}
          </div>

          {/* Conversion Funnel Visual */}
          <div className="bg-[#0a0a0a] rounded-xl p-5 border border-white/5">
            <h4 className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-4">Funnel Conversione Referral</h4>
            <div className="flex items-center justify-center gap-2 py-4">
              {[
                { label: 'Invitati', value: referralData.total_invited, color: '#0055FF', width: 100 },
                { label: 'Registrati', value: referralData.registered, color: '#F59E0B', width: referralData.total_invited > 0 ? Math.max(30, (referralData.registered / referralData.total_invited) * 100) : 30 },
                { label: 'Convertiti', value: referralData.converted, color: '#10B981', width: referralData.total_invited > 0 ? Math.max(20, (referralData.converted / referralData.total_invited) * 100) : 20 }
              ].map((step, i) => (
                <React.Fragment key={i}>
                  <div className="text-center" style={{ width: `${step.width}%`, minWidth: '80px' }}>
                    <div className="h-16 rounded-lg flex items-center justify-center mb-2 transition-all" style={{ backgroundColor: step.color + '20', borderLeft: `3px solid ${step.color}` }}>
                      <span className="text-xl font-['Outfit'] font-bold" style={{ color: step.color }}>{step.value}</span>
                    </div>
                    <span className="text-[10px] text-[#A1A1AA]">{step.label}</span>
                  </div>
                  {i < 2 && <ArrowRight className="w-4 h-4 text-[#A1A1AA] flex-shrink-0 mt-[-16px]" />}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Credits Earned */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
              <h4 className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-3">Crediti Guadagnati</h4>
              <div className="flex items-baseline gap-2 mb-4">
                <span className="text-3xl font-['Outfit'] font-bold text-[#F59E0B]">{referralData.credits_earned.toLocaleString()}</span>
                <span className="text-xs text-[#A1A1AA]">nel periodo</span>
              </div>
              <div className="text-xs text-[#A1A1AA] space-y-1">
                <div className="flex justify-between"><span>Totale storico</span><span className="text-white font-medium">{referralData.all_time_credits.toLocaleString()}</span></div>
                <div className="flex justify-between"><span>Referral totali (storico)</span><span className="text-white font-medium">{referralData.all_time_total}</span></div>
                <div className="flex justify-between"><span>Convertiti (storico)</span><span className="text-white font-medium">{referralData.all_time_converted}</span></div>
              </div>
            </div>

            {/* Referral Timeline */}
            <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
              <h4 className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-3">Timeline Referral</h4>
              {referralData.timeline?.length > 0 ? (
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={referralData.timeline.map(t => ({ ...t, date: t.date.substring(5) }))}>
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#A1A1AA' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: '#A1A1AA' }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="referrals" name="Invitati" fill="#0055FF" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="conversions" name="Convertiti" fill="#10B981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[160px] flex items-center justify-center text-[#A1A1AA] text-sm">Nessun dato referral</div>
              )}
            </div>
          </div>

          {/* Top Referrers (admin only) */}
          {referralData.top_referrers?.length > 0 && (
            <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
              <h4 className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-3">Top Referrer</h4>
              <div className="space-y-2">
                {referralData.top_referrers.map((r, i) => (
                  <div key={i} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0">
                    <span className="w-6 h-6 rounded-full bg-[#F59E0B]/20 flex items-center justify-center text-xs font-bold text-[#F59E0B]">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm truncate block">{r.company || r.email}</span>
                      <span className="text-[10px] text-[#A1A1AA]">{r.email}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-medium block">{r.converted}/{r.total}</span>
                      <span className="text-[10px] text-[#F59E0B]">{r.credits} crediti</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ===== HOURLY SECTION ===== */}
      {activeSection === 'hourly' && (
        <>
          <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
            <h4 className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-4">Distribuzione Oraria (ultimi {Math.min(period, 30)}gg)</h4>
            {hourlyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={hourlyData.map(h => ({ ...h, hour: `${h.hour}:00` }))}>
                  <XAxis dataKey="hour" tick={{ fontSize: 9, fill: '#A1A1AA' }} axisLine={false} tickLine={false} interval={1} />
                  <YAxis tick={{ fontSize: 10, fill: '#A1A1AA' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="plugin" name="Plugin API" fill="#0055FF" radius={[2, 2, 0, 0]} stackId="a" />
                  <Bar dataKey="b2b" name="B2B API" fill="#6366F1" radius={[2, 2, 0, 0]} stackId="a" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-[#A1A1AA] text-sm">Nessun dato orario</div>
            )}
          </div>

          {/* Peak hours */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(() => {
              const sorted = [...hourlyData].sort((a, b) => b.total - a.total);
              const peak = sorted[0];
              const totalCalls = hourlyData.reduce((s, h) => s + h.total, 0);
              const avgPerHour = totalCalls > 0 ? Math.round(totalCalls / 24) : 0;
              return [
                { label: 'Ora di Picco', value: peak ? `${peak.hour}:00` : '--', sub: peak ? `${peak.total} chiamate` : '', color: '#EF4444' },
                { label: 'Media Oraria', value: avgPerHour, sub: 'chiamate/ora', color: '#0055FF' },
                { label: 'Totale Periodo', value: totalCalls, sub: 'chiamate totali', color: '#10B981' }
              ];
            })().map((s, i) => (
              <div key={i} className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
                <span className="text-[10px] text-[#A1A1AA] uppercase tracking-wider block mb-1">{s.label}</span>
                <span className="text-2xl font-['Outfit'] font-semibold block" style={{ color: s.color }}>{typeof s.value === 'number' ? s.value.toLocaleString() : s.value}</span>
                <span className="text-[10px] text-[#A1A1AA]">{s.sub}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ===== ENDPOINTS SECTION ===== */}
      {activeSection === 'endpoints' && endpointData && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Plugin Endpoints */}
            <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
              <h4 className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-4">Endpoint Plugin API</h4>
              {endpointData.plugin_endpoints?.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={endpointData.plugin_endpoints.slice(0, 8)} layout="vertical">
                      <XAxis type="number" tick={{ fontSize: 10, fill: '#A1A1AA' }} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 9, fill: '#A1A1AA' }} width={120} axisLine={false} tickLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="count" name="Chiamate" fill="#0055FF" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="space-y-1 mt-3">
                    {endpointData.plugin_endpoints.map((ep, i) => (
                      <div key={i} className="flex items-center justify-between text-[10px] py-1 border-b border-white/5 last:border-0">
                        <span className="text-[#A1A1AA]">{ep.name}</span>
                        <div className="flex items-center gap-3">
                          <span className="font-medium">{ep.count}</span>
                          <span className="text-[#A1A1AA]">{ep.last_used?.substring(0, 10)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-[#A1A1AA] text-sm">Nessun dato</div>
              )}
            </div>

            {/* B2B Endpoints */}
            <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
              <h4 className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-4">Endpoint B2B API</h4>
              {endpointData.b2b_endpoints?.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={endpointData.b2b_endpoints.slice(0, 8)} layout="vertical">
                      <XAxis type="number" tick={{ fontSize: 10, fill: '#A1A1AA' }} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 9, fill: '#A1A1AA' }} width={120} axisLine={false} tickLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="count" name="Chiamate" fill="#6366F1" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="space-y-1 mt-3">
                    {endpointData.b2b_endpoints.map((ep, i) => (
                      <div key={i} className="flex items-center justify-between text-[10px] py-1 border-b border-white/5 last:border-0">
                        <span className="text-[#A1A1AA]">{ep.name}</span>
                        <div className="flex items-center gap-3">
                          <span className="font-medium">{ep.count}</span>
                          <span className="text-[#A1A1AA]">{ep.last_used?.substring(0, 10)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-[#A1A1AA] text-sm">Nessun dato B2B</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
